// Background image warm-up.
//
// Sections used to fetch their artwork the moment the player opened
// them, so on a weak connection Discover / roulette / auction /
// collections all stalled on first visit. We already prefetch their
// JSON at boot, so right after that we walk the payloads and pull the
// images into the browser cache during idle time, a few at a time, so
// the decode happens before the player ever navigates there.

const warmed = new Set();
const queue = [];
let active = 0;
const MAX_PARALLEL = 3;

const pump = () => {
  while (active < MAX_PARALLEL && queue.length) {
    const url = queue.shift();
    active += 1;
    const img = new Image();
    img.decoding = 'async';
    try { img.fetchPriority = 'low'; } catch { /* older browsers */ }
    const done = () => { active -= 1; pump(); };
    img.onload = done;
    img.onerror = done;
    img.src = url;
  }
};

const onIdle = (cb) => {
  if (typeof window === 'undefined') return;
  if (window.requestIdleCallback) window.requestIdleCallback(cb, { timeout: 2000 });
  else setTimeout(cb, 400);
};

export const warmImages = (urls) => {
  const fresh = [];
  (urls || []).forEach((url) => {
    if (typeof url !== 'string' || !url || warmed.has(url)) return;
    // Bound the bookkeeping set so a long session can't grow it forever.
    if (warmed.size > 600) warmed.clear();
    warmed.add(url);
    fresh.push(url);
  });
  if (!fresh.length) return;
  onIdle(() => {
    queue.push(...fresh);
    pump();
  });
};

const IMAGE_KEYS = ['image_url', 'thumb_url', 'cover_url', 'photo_url'];
const GRID_WIDTH = 320;
const ROW_WIDTH = 160;

// Walks any API payload and collects every artwork/avatar URL it holds.
export const collectImageUrls = (node, out = [], depth = 0) => {
  if (!node || depth > 5 || out.length > 300) return out;
  if (Array.isArray(node)) {
    node.forEach((item) => collectImageUrls(item, out, depth + 1));
    return out;
  }
  if (typeof node !== 'object') return out;
  IMAGE_KEYS.forEach((key) => {
    const value = node[key];
    if (typeof value === 'string' && value) out.push(value);
  });
  Object.keys(node).forEach((key) => {
    const value = node[key];
    if (value && typeof value === 'object') collectImageUrls(value, out, depth + 1);
  });
  return out;
};

export const warmFromPayloads = (payloads) => {
  const urls = [];
  (payloads || []).forEach((payload) => collectImageUrls(payload, urls));
  // Warm the variants the lists actually request, not the originals —
  // that keeps the background traffic small enough to finish on a weak
  // connection before the player navigates.
  const variants = [];
  urls.forEach((url) => {
    if (typeof url === 'string' && url.includes('/api/images/')) {
      const sep = url.includes('?') ? '&' : '?';
      variants.push(`${url}${sep}w=${ROW_WIDTH}`);
      variants.push(`${url}${sep}w=${GRID_WIDTH}`);
    } else {
      variants.push(url);
    }
  });
  warmImages(variants);
};
